import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Newspaper } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function resolveCover(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND}${url}`;
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function TeaserCard({ post }) {
  const cover = resolveCover(post.cover_url);
  const date = formatDate(post.published_at || post.created_at);
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`home-blog-card-${post.slug}`}
      className="group flex flex-col rounded-2xl border border-line bg-white overflow-hidden hover:border-royal/30 hover:shadow-sm transition-all"
    >
      <div className="aspect-[16/9] bg-bg-alt overflow-hidden">
        {cover ? (
          <img src={cover} alt={post.title} className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform" loading="lazy" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-royal/20">
            <Newspaper size={36} />
          </div>
        )}
      </div>
      <div className="flex flex-col flex-1 p-5">
        {post.category && (
          <span className="font-mono-ui text-[10.5px] uppercase tracking-[0.2em] text-royal">
            {post.category}
          </span>
        )}
        <h3 className="font-display text-lg font-bold text-ink mt-2 leading-tight line-clamp-2">
          {post.title}
        </h3>
        {post.excerpt && (
          <p className="mt-2 text-[13.5px] text-ink-soft leading-relaxed line-clamp-2 flex-1">
            {post.excerpt}
          </p>
        )}
        <span className="mt-4 inline-flex items-center gap-1 text-[12.5px] font-semibold text-royal-deep group-hover:text-royal">
          Read article <ArrowUpRight size={13} />
        </span>
      </div>
    </Link>
  );
}

export default function BlogTeaser() {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND}/api/public/blogs?limit=4`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => { if (!cancelled) setPosts(Array.isArray(d.items) ? d.items.slice(0, 4) : []); })
      .catch(() => { if (!cancelled) setPosts([]); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // Don't render an empty section when there are no posts yet.
  if (loaded && posts.length === 0) return null;
  if (!loaded) return null;

  return (
    <section data-testid="home-blog-teaser" className="py-6 lg:py-8 bg-bg-alt">
      <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-royal">
              08 · From the blog
            </p>
            <h2 className="font-display text-2xl md:text-3xl lg:text-[34px] font-bold text-ink mt-3 leading-[1.08] text-balance max-w-2xl">
              Latest insights for enterprise learners.
            </h2>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 rounded-md border border-line bg-white px-4 py-2.5 text-sm font-semibold text-royal-deep hover:border-royal"
          >
            View all posts <ArrowRight size={14} />
          </Link>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {posts.map((p) => (
            <TeaserCard key={p.id || p.slug} post={p} />
          ))}
        </div>
      </div>
    </section>
  );
}
